import React, { useState, useEffect, useRef } from "react";
import { BrowserRouter, Route, Routes, Navigate, Outlet } from "react-router-dom"; 
import { useDispatch, useSelector } from "react-redux";
import Box from "@mui/material/Box";
import ScopedCssBaseline from "@mui/material/ScopedCssBaseline";
import Grid from "@mui/material/Grid";
import axios from "axios";

// --- Components ---
import OEForm from "./components/OEForm/index";
import Header from "./components/Header/Header";
import CheckOut from "./components/OEForm/Ordering";
import ConfirmOrder from "./components/OEForm/ConfirmOrder";
import SelectDivision from "./components/OEForm/SelectDivsion";
import Register from "./components/Register/Register"; 
import Forgotpassword from "./components/Login/Forgotpassword/Forgotpassword";
import Login from "./components/Login/Login";
import Changepassword from './components/Changepassword/Changepassword'
import Home from './components/Home/Home'
import AjkCustomer from "./components/Ajk/AjkCustomer";
import EmailAdmin from "./components/AccountAdmin/EmailAdmin/EmailAdmin";
// --- Store ---
import SessionArray from "./Store/SessionArray";
import sessionId from "./Store/sessionId";
import LastPageSession from "./Store/LastPageSession";
import PageIDSession from "./Store/PageIDSession";
import SchemaIDSession from "./Store/SchemaIDSession";
import LabelSession from "./Store/LabelSession";
import userSlice from "./Store/userSlice"; // <--- IMPORT USER SLICE

// --- Constants & Helpers ---
import {
  RedirectUrl,
  requestOptions,
  VIPSDataServiceURL
} from "./Constants/GlobalConstant";
import { getMessage } from "./CommonFuntion/messages";

function App(queryParams) {
  // We keep local 'post' state for App's internal logic (like checking isValid),
  // but we won't pass it to Home/ChangePassword anymore.
  const [post, setPost] = useState("");
  const [menuData, setMenuData] = useState([]);
  const dispatch = useDispatch();
  const firstUpdate = useRef(true);

  // --- API Headers ---
  const apiHeaders = {
    'Authorization': process.env.REACT_APP_AUTHORIZATION,
    'ReactApiKey': process.env.REACT_APP_API_KEY
  };

  const {
    session_id,
    LastPage,
    PageID,
    Label,
    Inprogress,
    schemaID: schemaIDParam,
    schema_id: schema_idFallback,
    OCC,
  } = queryParams.queryParams;

  const schemaID = schemaIDParam || schema_idFallback;

  // --- Helpers ---
  const handleSessionData = (data) => {
    // 1. Existing Dispatch
    dispatch(
      SessionArray.actions.saveSessionArray({
        paxWebSessionStrings: data.paxWebSessionStrings,
      })
    );
    
    // 2. NEW: Save full user object to Redux User Slice
    // This allows Home and ChangePassword to access it via useSelector
    dispatch(userSlice.actions.saveUser(data)); 

    dispatch(sessionId.actions.saveSessionId({ sessionId: session_id }));
    dispatch(
      LastPageSession.actions.saveLastPageSession({ LastPageSession: LastPage })
    );
    dispatch(
      PageIDSession.actions.savePageIDSession({ PageIDSession: PageID })
    );
    dispatch(
      SchemaIDSession.actions.saveSchemaIDSession({ SchemaIDSession: schemaID })
    );
    const normalizedLabelSession = Array.isArray(Label)
      ? Label
      : Label
      ? [Label]
      : [];
    dispatch(
      LabelSession.actions.saveLabelSession({
        LabelSession: normalizedLabelSession,
      })
    );
  };

  const handleSessionExpired = () => {
    alert(getMessage("sessionExpired"));
    const path = Inprogress
      ? process.env.REACT_APP_OCC_ORDER_INPROGRESS_SESSION_EXPIRED_URL
      : process.env.REACT_APP_OCC_SESSION_EXPIRED_URL;
    window.location.href = path;
  };

  // --- Effect: Fetch Session ---
  useEffect(() => {
    if (firstUpdate.current && session_id) {
      firstUpdate.current = false;
      
      fetch(
        `${VIPSDataServiceURL}/Authentication?session_id=${session_id}`,
        requestOptions,
        {
          mode: "no-cors",
        }
      )
        .then((response) => response.json())
        .then((data) => {
        handleSessionData(data);
        setPost(data); // Local state for App.js usage
        })
        .catch((error) => console.error("Error fetching session data:", error));
      } else {
      firstUpdate.current = false;
    }
  }, [session_id, LastPage, PageID, schemaID, Label]);

  // --- Effect: Check Session Validity ---
  useEffect(() => {
    if (post && post.isValid === false) {
      if (OCC === undefined) {
        window.location.href = RedirectUrl;
      } else {
        handleSessionExpired();
      }
    }
  }, [post, OCC, Inprogress]);
    
  // --- Effect: Fetch Menus ---
  useEffect(() => {
      const sessionStrings = post?.paxWebSessionStrings || [];
      const userIdItem = sessionStrings.find(item => item.Key === 'user_id');
      const userId = userIdItem ? userIdItem.Value : null;

      if (post && userId) {
      const fetchMenu = async () => {
        try {
          const baseUrl = VIPSDataServiceURL.replace(/\/$/, "");
                  const fullUrl = `${baseUrl}/Menu/GetMenus?user_id=${userId}`;
                  
                  const response = await axios.post(fullUrl, null, { headers: apiHeaders });
                  
                  const menuJson = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
          setMenuData(menuJson);

        } catch (error) {
          console.error("Failed to load user menus:", error);
          setMenuData([]);
        }
      };
fetchMenu();
}
  }, [post]); 

  const prefix = process.env.REACT_APP_ORDERING_PATH 
    ? (process.env.REACT_APP_ORDERING_PATH.startsWith('/') ? process.env.REACT_APP_ORDERING_PATH : '/' + process.env.REACT_APP_ORDERING_PATH)
    : "/OrderingDev";

  // --- Layouts ---
  const MainLayout = () => (
    <>
      <Grid item xs={12}>
        {post && (
          <Header
            post={post} // Header likely still uses props, keeping it safe
            session_id={session_id}
            LastPageSession={LastPageSession}
            menuData={menuData}
          />
        )}
      </Grid>
      <Grid item xs={12} id="content">
        <Outlet />
      </Grid>
    </>
  );

  const AuthLayout = () => (
    <Grid item xs={12} id="content">
      <Outlet />
    </Grid>
  );

  return (
    <React.Fragment>
      <ScopedCssBaseline>
        <Box sx={{ flexGrow: 1 }}>
          <BrowserRouter>
            <Grid container spacing={2}>
              <Routes>
                {/* --- PUBLIC ROUTES --- */}
                <Route element={<AuthLayout />}>
                  <Route path={`${prefix}/login`} element={<Login />} />
                  <Route path={`${prefix}/register`} element={<Register />} />
                  <Route path={`${prefix}/forgotpassword`} element={<Forgotpassword />} />
                                  </Route>

                {/* --- PROTECTED ROUTES --- */}
                <Route element={<MainLayout />}>
                                    
                <Route 
                    path={`${prefix}/home`} 
                    element={post ? <Home /> : " "} 
                  />
                  
                  <Route 
                    path={`${prefix}/changepassword`} 
                    element={post ? <Changepassword /> :" "} 
                  />

                  <Route 
                    path={`${prefix}/Ajk/ajkcustomer`} 
                    element={post ? <AjkCustomer/> : " "} 
                  />
                   <Route 
                    path={`${prefix}/accountadmin/emailadmin`} 
                    element={post ? <EmailAdmin/> : " "} 
                  />
                  <Route
                    path={`${prefix}/SelectDivision`}
                    element={post ? <SelectDivision post={post} /> : <Login />}
                  />
                  <Route
                    path={`${prefix}/CheckOut`}
                    element={post ? <CheckOut post={post} /> : <Login />}
                  />
                  <Route
                    path={`${prefix}/ConfirmOrder`}
                    element={post ? <ConfirmOrder /> : <Login />}
                  />
                  <Route
                    path={`${prefix}`}
                    element={post ? <OEForm post={post}/> : ""}
                  />
                </Route>

                <Route
                  path="*"
                  element={<Navigate to={`${prefix}/login`} />}
                />
              </Routes>
            </Grid>
          </BrowserRouter>
        </Box>
      </ScopedCssBaseline>
    </React.Fragment>
  );
}

export default App;